import ButtonGroup from "@visa/vds/button-group";
import ButtonText from "@visa/vds/button-text";
import Col from "@visa/vds/col";
import DateSelector from "@visa/vds/date-selector";
import Divider from "@visa/vds/divider";
import Form from "@visa/vds/form";
import FormControl from "@visa/vds/form-control";
import Row from "@visa/vds/row";
import Typography from "@visa/vds/typography";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import MCSControlInput from "../../../../components/MCSControlInput";
import { FormHelper } from "../../../../_helpers/FormHelper";
import SearchFieldTooltip from "./SearchFieldTooltip";

const LibraryAccessHistorySearchPanel = (props) => {
  const { control, handleSubmit, setError, getValues, trigger } = useForm({
    filter: props.filter,
  });

  const onSubmit = (data, e) => {
    props.handleClickSearch();
  };

  const updateFormData = () => {
    props.setFilter(getValues());
  };

  return (
    <>
      <Row>
        <Col>
          <Typography tag="h4" variant="subtitle">
            Filtering Options
          </Typography>
          <Divider type="standard" />
        </Col>
      </Row>
      <Form style={{ width: "100%" }} onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={4} sm={8} md={8} lg={6} xl={4}>
            <FormControl position="left" space="fifty">
              <Controller
                name="dateFrom"
                control={control}
                rules={{
                  validate: {
                    isDateValid: (v) => {
                      return (
                        v == null ||
                        getValues("dateTo") == null ||
                        v <= getValues("dateTo") ||
                        "Date From cannot be greater than Date To"
                      );
                    },
                    noFutureDate: (v) => {
                      return (
                        v == null ||
                        v <= new Date() ||
                        "Cannot be a future date"
                      );
                    },
                  },
                }}
                render={({ field, fieldState }) => (
                  <DateSelector
                    maxDate={new Date()}
                    label={"Accessed Date From"}
                    name="start-date-picker"
                    onSelect={(date) => {
                      field.onChange(date != null ? new Date(date) : null);
                      trigger("dateTo");
                      updateFormData();
                    }}
                    selected={field.value}
                    invalid={fieldState.invalid}
                    showErrorText={fieldState.invalid}
                    errorText={FormHelper.renderErrorMsg(fieldState.error)}
                  />
                )}
              />
            </FormControl>
            <FormControl position="right" space="fifty">
              <Controller
                name="dateTo"
                control={control}
                rules={{
                  max: new Date(),
                  validate: {
                    isDateValid: (v) => {
                      return (
                        v == null ||
                        getValues("dateFrom") == null ||
                        v >= getValues("dateFrom") ||
                        "Date To cannot be smaller than Date From"
                      );
                    },
                    noFutureDate: (v) => {
                      return (
                        v == null ||
                        v <= new Date() ||
                        "Cannot be a future date"
                      );
                    },
                  },
                }}
                render={({ field, fieldState }) => (
                  <DateSelector
                    maxDate={new Date()}
                    label={"Accessed Date To"}
                    name="end-date-picker"
                    onSelect={(date) => {
                      field.onChange(date != null ? new Date(date) : null);
                      trigger("dateFrom");
                      updateFormData();
                    }}
                    selected={field.value}
                    invalid={fieldState.invalid}
                    showErrorText={fieldState.invalid}
                    errorText={FormHelper.renderErrorMsg(fieldState.error)}
                  />
                )}
              />
            </FormControl>
          </Col>
          <Col xs={4} sm={8} md={8} lg={6} xl={8}>
            <FormControl position="left" space="thirty">
              <MCSControlInput
                name="userId"
                control={control}
                handleChange={updateFormData}
                rules={{
                  required: false,
                  maxLength: 20,
                  validate: {
                    isEmpty: (v) =>
                      FormHelper.isStringBlank(v) ||
                      "The field cannot be blank",
                  },
                }}
                label={
                  <SearchFieldTooltip
                    label={"User ID"}
                    text={'Field is "free form text"'}
                    ariaLabel="User ID"
                  />
                }
              />
            </FormControl>
            <FormControl position="right" space="seventy">
              <div className="vds-input vds-input--fullwidth">
                <SearchFieldTooltip
                  label={"Organization Name"}
                  text={"Organizations from Results"}
                />
                <div className="vds-input-bar">
                  {props.orgList && (
                    <Controller
                      name="organizationId"
                      control={control}
                      render={({ field, fieldState }) => (
                        <Select
                          selected={field.value}
                          styles={props.selectCustomStyles}
                          isClearable={true}
                          options={props.orgList}
                          placeholder="- Select -"
                          onChange={(e) => {
                            field.onChange(e != null ? e.value : null);
                            updateFormData();
                          }}
                          disabled={props.orgList.length === 0}
                        />
                      )}
                    />
                  )}
                </div>
              </div>
            </FormControl>
          </Col>
        </Row>
        <Row>
          <Col xs={4} sm={8} md={8} lg={12} xl={12}>
            <FormControl position="left" space="fifty">
              <MCSControlInput
                name="libraryPostTitle"
                control={control}
                rules={{
                  maxLength: 50,
                  required: false,
                  validate: {
                    isEmpty: (v) =>
                      FormHelper.isStringBlank(v) ||
                      "The field cannot be blank",
                  },
                }}
                handleChange={updateFormData}
                label={
                  <SearchFieldTooltip
                    label={"Display Title"}
                    text={'Field is "free form text"'}
                  />
                }
                ariaLabel="Title"
              />
            </FormControl>
            <FormControl position="right" space="fifty">
              <MCSControlInput
                name="libraryCategoryName"
                control={control}
                rules={{
                  maxLength: 50,
                  required: false,
                  validate: {
                    isEmpty: (v) =>
                      FormHelper.isStringBlank(v) ||
                      "The field cannot be blank",
                  },
                }}
                handleChange={updateFormData}
                maxLength="50"
                label={
                  <SearchFieldTooltip
                    label={"Category"}
                    text={'Field is "free form text"'}
                  />
                }
                ariaLabel="Category"
              />
            </FormControl>
          </Col>
        </Row>
        <Row>
          <Col xs={4} sm={8} md={8} lg={12}>
            <ButtonGroup direction={"right"}>
              <ButtonText type="submit" colorScheme="primary">
                Show Results
              </ButtonText>
              {props.enableExport && (
                <ButtonText
                  onClick={props.handleClickExport}
                  colorScheme="secondary"
                >
                  Export
                </ButtonText>
              )}
            </ButtonGroup>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default LibraryAccessHistorySearchPanel;
